/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 13th 2022, 12:12:32 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { ChoiceIcon, DeadlineIcon } from "theme/icons";
import { useSaaSSubscriptionAPI } from "saas-subscription/services/SubscriptionAPI";
import { SubscriptionPlans } from "saas-subscription/components/SubscriptionPlans";
import { OSLoadingButton, FullScreenNotificationGrid, EmphasisText, CircularProgressCentered } from "ui-widgets";
import { Organization } from "lib/schemas";

interface SubscriptionExpiredCTAProps {
  organization: Organization;
}

export const SubscriptionExpiredCTA = ({ organization }: SubscriptionExpiredCTAProps) => {
  const { useGetOrganizationSubscription } = useSaaSSubscriptionAPI();
  const { subscriptionData, isMakingAPIRequest: isFetchingSubscriptionData } = useGetOrganizationSubscription(
    organization?.id
  );

  return isFetchingSubscriptionData ? (
    <CircularProgressCentered />
  ) : (
    <>
      {subscriptionData?.subscription_id ? (
        <UpdatePaymentDetailsCTA organization={organization} />
      ) : (
        <RenewPlanCTA organization={organization} />
      )}
    </>
  );
};

const RenewPlanCTA = ({ organization }) => {
  const organizationName = organization?.name;

  return (
    <FullScreenNotificationGrid
      pageTitle={
        <>
          Your Subscription for <EmphasisText>{organizationName}</EmphasisText> has expired.
        </>
      }
      pageContent={
        <p>
          Kindly renew your subscription for <EmphasisText>{organizationName}</EmphasisText> to continue using Onestop.
        </p>
      }
      pageIllustration={<ChoiceIcon height={128} width={128} />}
      addtionalGridItems={
        <Grid item xs={12}>
          <SubscriptionPlans />
        </Grid>
      }
    />
  );
};

const UpdatePaymentDetailsCTA = ({ organization }) => {
  const { useCreateSetupCheckoutSession } = useSaaSSubscriptionAPI();
  const { createSetupCheckout, isMakingAPIRequest: isCheckingOut } = useCreateSetupCheckoutSession(organization);

  const organizationName = organization?.name;

  return (
    <FullScreenNotificationGrid
      pageTitle={
        <>
          Your Subscription for <EmphasisText>{organizationName}</EmphasisText> has expired.
        </>
      }
      pageContent={
        <>
          <p>
            We have been unable to charge you for your subscription. Kindly ensure your provided payment method has
            sufficient funds or update your payment details to continue using Onestop.
          </p>
          <p>
            If we are unable to charge your account after more attempts, your subscription will be automically cancelled
            and your organization <EmphasisText>{organizationName}</EmphasisText> will no longer be available on
            Onestop.
          </p>
          <p>
            <Stack direction="row" spacing={2} justifyContent="center">
              <OSLoadingButton isLoading={isCheckingOut} variant="contained" onClick={createSetupCheckout} size="large">
                Update Payment Details
              </OSLoadingButton>
            </Stack>
          </p>
        </>
      }
      pageIllustration={<DeadlineIcon height={128} width={128} />}
    />
  );
};
